/**
 * AthleteCheckIn Component
 *
 * Token-based form for athletes to log session feedback after training.
 * Accessed via SMS link - no authentication required (token validates access).
 *
 * Features:
 * - Loads session data via token validation
 * - Four metric sliders (soreness, motivation, mental fatigue, RPE)
 * - Optional notes field
 * - "I wasn't at this session" checkbox
 * - Pre-fills existing reports (allows updates)
 * - Mobile-optimized UI
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { MetricSlider } from '@/components/shared/MetricSlider';
import { getApiUrl } from '@/config/api';
import { Dumbbell, Flame, Brain, Activity } from '@/utils/icons';
import type { SessionTokenResponse, AthleteReportSubmission } from '@/types/sessions';

export const AthleteCheckIn: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Page state
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionTokenResponse['session'] | null>(null);
  const [athleteId, setAthleteId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state - default to middle values (5)
  const [soreness, setSoreness] = useState(5);
  const [motivation, setMotivation] = useState(5);
  const [mentalFatigue, setMentalFatigue] = useState(5);
  const [sessionRpe, setSessionRpe] = useState(5);
  const [notes, setNotes] = useState('');
  const [attended, setAttended] = useState(true);

  /**
   * Fetch session data on component mount.
   * Validates token and loads session details + any existing report.
   */
  useEffect(() => {
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, token]);

  const fetchSession = async () => {
    if (!token) {
      setError('No token provided. Please use the link from your SMS message.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        getApiUrl(`/api/teams/session-token/athlete/${sessionId}/?token=${token}`)
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Invalid or expired link');
        setTimeout(() => navigate('/'), 3000);  // Redirect after 3 seconds
        return;
      }

      const data: SessionTokenResponse = await response.json();

      setSession(data.session);
      setAthleteId(data.athlete_id);

      // Pre-fill form if athlete already submitted a report
      if (data.existing_report) {
        setSoreness(data.existing_report.soreness);
        setMotivation(data.existing_report.motivation);
        setMentalFatigue(data.existing_report.mental_fatigue);
        setSessionRpe(data.existing_report.session_rpe);
        setNotes(data.existing_report.notes || '');
        setAttended(data.existing_report.attended);
      }

    } catch (err) {
      console.error('Error fetching session:', err);
      setError('Error loading session. Please try again or contact your coach.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form submission.
   * Submits athlete feedback via token-based endpoint.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload: AthleteReportSubmission = {
      soreness,
      motivation,
      mental_fatigue: mentalFatigue,
      session_rpe: sessionRpe,
      notes,
      attended,
    };

    try {
      const response = await fetch(
        getApiUrl(`/api/teams/session-token/athlete/${sessionId}/submit/?token=${token}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Error submitting check-in. Please try again.');
        setSubmitting(false);
        return;
      }

      // Success!
      alert('✅ Check-in complete! Thanks for your feedback.');
      // Could redirect or show success state
      // For now, stay on page to allow viewing/editing
      setSubmitting(false);

    } catch (err) {
      console.error('Error submitting check-in:', err);
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--spinner-color)] mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading session...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Unable to Load Session
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'Session not found'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="cgm-button px-6 py-2 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            How did today's training feel?
          </h1>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Quick 30-second check-in to help your coach optimize training
          </p>

          {/* Session Details Badge */}
          <div className="bg-[var(--accent-ui-subtle)] p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              📅 {new Date(session.session_date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              ⏱️ {session.duration_minutes} minutes •
              🏃 {session.primary_focus.charAt(0).toUpperCase() + session.primary_focus.slice(1)}
            </p>
            {session.coach_name && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Coach: {session.coach_name}
              </p>
            )}
          </div>
        </div>

        {/* Check-In Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          {/* Attended Checkbox */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!attended}
                onChange={(e) => setAttended(!e.target.checked)}
                className="w-5 h-5 text-[var(--accent-ui)] border-gray-300 dark:border-gray-600 rounded focus:ring-[var(--accent-ui-focus)] focus:ring-2"
              />
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                I wasn't at this session
              </span>
            </label>
          </div>

          {/* Only show sliders if athlete attended */}
          {attended && (
            <>
              {/* Soreness Slider */}
              <MetricSlider
                label="Soreness Level"
                icon={Dumbbell}
                min={1}
                max={10}
                value={soreness}
                onChange={setSoreness}
                minLabel="None"
                maxLabel="Extreme"
                helpText="How sore are you feeling right now?"
              />

              {/* Motivation Slider */}
              <MetricSlider
                label="Motivation"
                icon={Flame}
                min={1}
                max={10}
                value={motivation}
                onChange={setMotivation}
                minLabel="Very Low"
                maxLabel="Very High"
                helpText="How motivated did you feel during the session?"
              />

              {/* Mental Fatigue Slider */}
              <MetricSlider
                label="Mental Fatigue"
                icon={Brain}
                min={1}
                max={10}
                value={mentalFatigue}
                onChange={setMentalFatigue}
                minLabel="Fresh"
                maxLabel="Exhausted"
                helpText="How mentally tired are you?"
              />

              {/* Session RPE Slider */}
              <MetricSlider
                label="Session Difficulty (RPE)"
                icon={Activity}
                min={1}
                max={10}
                value={sessionRpe}
                onChange={setSessionRpe}
                minLabel="Very Easy"
                maxLabel="Maximal"
                helpText="How hard was this session for you?"
              />

              {/* Notes Textarea */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📝 Notes <span className="text-gray-500">(optional, max 200 characters)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[var(--accent-ui-focus)] focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Any additional thoughts about today's session..."
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {notes.length}/200 characters
                  </p>
                  {notes.length > 180 && (
                    <p className="text-xs text-orange-500">
                      {200 - notes.length} remaining
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300">
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[var(--text-primary)] text-[var(--bg-primary)] font-semibold py-3 px-6 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              '✓ Submit Check-in'
            )}
          </button>

          {/* Info Footer */}
          <div className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            Your feedback helps your coach optimize training and prevent injury
          </div>
        </form>

        {/* Help Card */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2 text-sm">
            💡 Why track session feedback?
          </h4>
          <ul className="text-xs text-blue-800 dark:text-blue-400 space-y-1">
            <li>• Helps coaches personalize your training intensity</li>
            <li>• Identifies early warning signs of overtraining</li>
            <li>• Tracks recovery patterns over time</li>
            <li>• Data-driven performance optimization</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
